import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { BiReceipt } from 'react-icons/bi';
import { CiUser } from 'react-icons/ci';
import { FaRegFileAlt } from 'react-icons/fa';
import { FiChevronDown, FiChevronUp, FiClock, FiFileText } from 'react-icons/fi';
import { GoHash } from 'react-icons/go';
import { IoCheckmarkDoneCircleOutline } from 'react-icons/io5';
import { LiaNotesMedicalSolid } from 'react-icons/lia';
import { MdOutlineFeaturedPlayList, MdOutlineWorkOutline } from 'react-icons/md';
import { PiRepeatFill } from 'react-icons/pi';
import { TbProgress } from 'react-icons/tb';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import ImageViewer from '../../components/ui/ImageViewer';
import InputField from '../../components/ui/InputField';
import PopupWrapper from '../../components/utils/PopupWrapper';
import {
  fetchDashboardMetrics,
  loadProjects,
} from '../../redux/actions/projectAction';
import {
  fetchUserProjects,
  fetchUserProjectsDashboardData,
} from '../../redux/actions/userProjectsAction';
import { AppDispatch, RootState } from '../../redux/store';
import { isAdmin } from '../../redux/types/authTypes';
import { extractCustomerName } from '../../utils/customerUtils';
import ResponsiveTable from '../Table/ResponsiveTable';
import DashboardFinancialRow from './DashboardFinancialRow';
import DashboardReopenRow from './DashboardReopenRow';
import DashboardStatsRow from './DashboardStatsRow';
import DashboardStatusRow from './DashboardStatusRow';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

// Define interface for date range
interface DateRange {
  startDate: Date;
  endDate: Date;
}

// Define interface for payment data
interface PaymentData {
  date: string;
  amount: number;
}

// Define interface for project data
interface ProjectData {
  id: string;
  name: string;
  status: string;
  startDate?: string;
  endDate?: string;
  customer?: {
    name: string;
    id?: string;
  };
  assignedTo?: {
    name: string;
    id?: string;
  };
  revisionNumber?: number;
  articleNo?: string;
  createdAt?: string;
  description?: string;
  projectRevisions?: Array<{
    id: string;
    revisionNumber: number;
    formattedRevisionNumber?: string;
  }>;
}

interface DashboardProps {
  dateRange?: DateRange;
  projectViewType?: 'all' | 'my';
}

// Define interface for project popup state
interface ProjectPopupState {
  isOpen: boolean;
  openPopup: () => void;
  closePopup: () => void;
  togglePopup: () => void;
}

// Custom hook for managing popup state
const usePopup = (initialState = false): ProjectPopupState => {
  const [isOpen, setIsOpen] = useState(initialState);

  const openPopup = () => setIsOpen(true);
  const closePopup = () => setIsOpen(false);
  const togglePopup = () => setIsOpen(prev => !prev);

  return {
    isOpen,
    openPopup,
    closePopup,
    togglePopup,
  };
};

// Helper function to check if user has finance access
const hasFinanceAccess = (user: any): boolean => {
  // Admin always has access
  if (isAdmin(user)) {
    return true;
  }

  // Check user permissions for financeAccess
  let hasAccess = false;

  if (user?.permissions) {
    // Handle different permission object formats
    if (typeof user.permissions === 'object' && '0' in user.permissions) {
      // Object with numeric keys like {0: {...}}
      hasAccess = Boolean(user.permissions[0]?.financeAccess);
    } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
      // Array format
      hasAccess = Boolean(user.permissions[0]?.financeAccess);
    } else if (typeof user.permissions === 'object') {
      // Direct object access
      hasAccess = Boolean(user.permissions.financeAccess);
    }
  }

  return hasAccess;
};

// Define status colors for project status display
const statusColors: { [key: string]: string } = {
  UNASSIGNED: 'bg-gray-100 text-gray-800',
  'UN-ASSIGNED': 'bg-gray-100 text-gray-800',
  ASSIGNED: 'bg-purple-100 text-purple-800',
  'IN-PROGRESS': 'bg-indigo-100 text-indigo-800',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-800',
  REVIEW: 'bg-amber-100 text-amber-800',
  DELIVERED: 'bg-green-100 text-green-800',
  BILLED: 'bg-cyan-100 text-cyan-800',
  'PAYMENT RECEIVED': 'bg-emerald-100 text-emerald-800',
  PAYMENT_RECEIVED: 'bg-emerald-100 text-emerald-800',
  REVISION: 'bg-purple-100 text-purple-800',
  REOPEN: 'bg-blue-100 text-blue-800',
  'ON-HOLD': 'bg-gray-100 text-gray-800',
  HOLD: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800',
};

// Define status icons for project status display
const statusIcons: { [key: string]: JSX.Element } = {
  UNASSIGNED: <GoHash />,
  'UN-ASSIGNED': <GoHash />,
  ASSIGNED: <BiReceipt />,
  'IN-PROGRESS': <TbProgress />,
  IN_PROGRESS: <TbProgress />,
  REVIEW: <LiaNotesMedicalSolid />,
  DELIVERED: <IoCheckmarkDoneCircleOutline />,
  BILLED: <BiReceipt />,
  'PAYMENT RECEIVED': <BiReceipt />,
  PAYMENT_RECEIVED: <BiReceipt />,
  REVISION: <PiRepeatFill />,
  REOPEN: <LiaNotesMedicalSolid />,
  'ON-HOLD': <GoHash />,
  HOLD: <GoHash />,
  CANCELLED: <IoCheckmarkDoneCircleOutline />,
  CANCEL: <IoCheckmarkDoneCircleOutline />,
};

const Dashboard: React.FC<DashboardProps> = ({ dateRange, projectViewType = 'all' }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    projectCount,
    lastProject,
    projects: allProjects,
    loading: projectsLoading,
    totalAmount,
    paymentReceived,
    billedAmount,
    pipelineAmount,
    statusCounts: reduxStatusCounts,
    dashboardMetrics,
    filteredProjects,
  } = useSelector((state: RootState) => state.projects);

  // Get user data for permission checks
  const { user } = useSelector((state: RootState) => state.auth);

  // Add early debug log for user detection
  useEffect(() => {
    if (user) {
      console.log('Dashboard detected user:', {
        id: user.id,
        role: user.role,
        designation: user.designation,
        isAdmin: isAdmin(user),
      });
    } else {
      console.warn('Dashboard - no user detected in auth state');
    }
  }, [user]);

  // Get user projects data
  const {
    userProjects,
    loading: userProjectsLoading,
    dashboardData,
    dashboardDataLoading,
    dashboardDataError,
  } = useSelector((state: RootState) => state.userProjects);

  // Get customers data for project filtering
  const { customers } = useSelector((state: RootState) => state.users);

  // State for view mode (global vs user-specific)
  // Initialize based on the projectViewType prop
  const [showUserStats, setShowUserStats] = useState(projectViewType === 'my');

  // Update showUserStats when projectViewType changes
  useEffect(() => {
    setShowUserStats(projectViewType === 'my');

    // When view type changes, make sure we're showing the right data
    // by updating the local state from the appropriate Redux state
    if (projectViewType === 'my' && dashboardData) {
      console.log('Switching to My Projects view, updating local state with user dashboard data');
      const paymentReceived = dashboardData.financialMetrics?.paymentReceivedSum;
      const billed = dashboardData.financialMetrics?.billedSum;
      const pipeline = dashboardData.financialMetrics?.pipelineSum;

      // Ensure we don't store NaN values
      setTotalPaymentAmount(isNaN(paymentReceived) ? 0 : paymentReceived || 0);
      setTotalBilledAmount(isNaN(billed) ? 0 : billed || 0);
      setTotalInProgressAmount(isNaN(pipeline) ? 0 : pipeline || 0);

      // Update status counts if available
      if (dashboardData.statusCounts) {
        setStatusCounts(dashboardData.statusCounts);
      }
    } else if (projectViewType === 'all' && dashboardMetrics) {
      console.log(
        'Switching to All Projects view, updating local state with all projects dashboard metrics'
      );
      const paymentReceived = dashboardMetrics.financialMetrics?.paymentReceivedSum;
      const billed = dashboardMetrics.financialMetrics?.billedSum;
      const pipeline = dashboardMetrics.financialMetrics?.pipelineSum;

      // Ensure we don't store NaN values
      setTotalPaymentAmount(isNaN(paymentReceived) ? 0 : paymentReceived || 0);
      setTotalBilledAmount(isNaN(billed) ? 0 : billed || 0);
      setTotalInProgressAmount(isNaN(pipeline) ? 0 : pipeline || 0);

      setStatusCounts(dashboardMetrics.statusCounts || {});
    }
  }, [projectViewType, dashboardData, dashboardMetrics]);

  // State for payment data
  const [paymentData, setPaymentData] = useState<PaymentData[]>([]);
  const [totalPaymentAmount, setTotalPaymentAmount] = useState<number>(0);
  const [totalBilledAmount, setTotalBilledAmount] = useState<number>(0);
  const [totalInProgressAmount, setTotalInProgressAmount] = useState<number>(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [loadingPaymentData, setLoadingPaymentData] = useState<boolean>(false);

  // State for long polling
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const POLLING_INTERVAL = 30000; // 30 seconds

  // State for project popup with custom hook
  const [selectedProject, setSelectedProject] = useState<any | null>(null);
  const projectDetailsPopup = usePopup();

  // State for assignment history
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);

  // State for image viewer
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState('');

  // Default date range (from 1st of current month to today) if not provided
  const currentDateRange = useMemo(() => {
    const defaultStartDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const defaultEndDate = new Date();
    return dateRange || { startDate: defaultStartDate, endDate: defaultEndDate };
  }, [dateRange]);

  // Function to fetch dashboard data (for both initial load and polling)
  const fetchDashboardData = useCallback(() => {
    // Format dates in local timezone to avoid UTC conversion issues
    const startDateStr = `${currentDateRange.startDate.getFullYear()}-${String(currentDateRange.startDate.getMonth() + 1).padStart(2, '0')}-${String(currentDateRange.startDate.getDate()).padStart(2, '0')}`;
    const endDateStr = `${currentDateRange.endDate.getFullYear()}-${String(currentDateRange.endDate.getMonth() + 1).padStart(2, '0')}-${String(currentDateRange.endDate.getDate()).padStart(2, '0')}`;

    console.log(`Fetching dashboard data for date range: ${startDateStr} to ${endDateStr}`);

    // Fetch global dashboard metrics (for All Projects view)
    dispatch(
      fetchDashboardMetrics({
        startDate: startDateStr,
        endDate: endDateStr,
        forceRefresh: true, // Always force refresh for polling
      })
    );

    // Fetch user-specific dashboard data (for My Projects view)
    if (user?.id) {
      dispatch(
        fetchUserProjectsDashboardData({
          startDate: startDateStr,
          endDate: endDateStr,
          forceRefresh: true, // Always force refresh for polling
        })
      );
    }

    // Update last updated timestamp
    setLastUpdated(new Date());
    setLoadingPaymentData(false);
  }, [currentDateRange, dispatch, user?.id]);

  useEffect(() => {
    // Load all projects
    dispatch(loadProjects({ page: 1, limit: 10 }));

    // If we have a user ID, make sure user projects are loaded
    if (user?.id) {
      console.log('Fetching user projects for user ID:', user.id);
      dispatch(fetchUserProjects({ userId: user.id, page: 1, limit: 10 }));
    } else {
      console.warn('No user ID available for fetching user projects');
    }
  }, [dispatch, user?.id, location.key]); // Using location.key to trigger reload on every navigation to dashboard

  // Use actual values from the Redux store (switch between user and global stats)
  const displayProjectCount = showUserStats
    ? dashboardData?.totalUserProjectsCount || 0
    : dashboardMetrics?.totalProjectsCount || projectCount || 0;

  const displayPaymentReceived = showUserStats
    ? dashboardData?.financialMetrics?.paymentReceivedSum || 0
    : dashboardMetrics?.financialMetrics?.paymentReceivedSum || paymentReceived || 0;

  const displayBilledAmount = showUserStats
    ? dashboardData?.financialMetrics?.billedSum || 0
    : dashboardMetrics?.financialMetrics?.billedSum || billedAmount || 0;

  const displayDueAmount = showUserStats
    ? dashboardData?.financialMetrics?.dueSum || 0
    : dashboardMetrics?.financialMetrics?.dueSum || 0;

  const displayPipelineAmount = showUserStats
    ? dashboardData?.financialMetrics?.pipelineSum || 0
    : dashboardMetrics?.financialMetrics?.pipelineSum || pipelineAmount || 0;

  // Format counts with commas
  const formattedProjectCount = displayProjectCount.toLocaleString();

  // Toggle between global and user-specific stats
  const toggleViewMode = () => {
    // Toggle the state regardless of projectViewType, since we rely on the parent
    // to set the initial state correctly
    setShowUserStats(prev => !prev);

    // Log what view we're switching to
    console.log(`Toggled to ${!showUserStats ? 'user-specific' : 'global'} stats`);

    // Log data sources to help debug
    console.log('User dashboard data:', dashboardData);
    console.log('Global dashboard metrics:', dashboardMetrics);

    // Update local state for financial metrics based on the toggled view
    if (showUserStats) {
      // Switching to All Projects view
      if (dashboardMetrics?.financialMetrics) {
        const paymentReceived = dashboardMetrics.financialMetrics.paymentReceivedSum;
        const billed = dashboardMetrics.financialMetrics.billedSum;
        const pipeline = dashboardMetrics.financialMetrics.pipelineSum;

        // Ensure we don't store NaN values
        setTotalPaymentAmount(isNaN(paymentReceived) ? 0 : paymentReceived || 0);
        setTotalBilledAmount(isNaN(billed) ? 0 : billed || 0);
        setTotalInProgressAmount(isNaN(pipeline) ? 0 : pipeline || 0);

        setStatusCounts(dashboardMetrics.statusCounts || {});
      }
    } else {
      // Switching to My Projects view
      if (dashboardData?.financialMetrics) {
        const paymentReceived = dashboardData.financialMetrics.paymentReceivedSum;
        const billed = dashboardData.financialMetrics.billedSum;
        const pipeline = dashboardData.financialMetrics.pipelineSum;

        // Ensure we don't store NaN values
        setTotalPaymentAmount(isNaN(paymentReceived) ? 0 : paymentReceived || 0);
        setTotalBilledAmount(isNaN(billed) ? 0 : billed || 0);
        setTotalInProgressAmount(isNaN(pipeline) ? 0 : pipeline || 0);

        // Update status counts if available
        if (dashboardData.statusCounts) {
          setStatusCounts(dashboardData.statusCounts);
        }
      }
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    // Handle NaN or undefined values by defaulting to 0
    const safeAmount = isNaN(amount) || amount === undefined ? 0 : amount;

    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
      .format(safeAmount)
      .replace('₹', '₹ '); // Add space after rupee symbol
  };

  // Add debug logging for the status counts objects
  useEffect(() => {
    console.log('Debug - reduxStatusCounts:', reduxStatusCounts);
    console.log('Debug - dashboardMetrics:', dashboardMetrics);
    console.log('Debug - dashboardData:', dashboardData);

    // Log specific status counts to help with debugging
    if (showUserStats && dashboardData?.statusCounts) {
      console.log('Debug - User Dashboard Status Counts:', dashboardData.statusCounts);
    } else if (!showUserStats && dashboardMetrics?.statusCounts) {
      console.log('Debug - All Projects Status Counts:', dashboardMetrics.statusCounts);
    }

    console.log('Debug - auth user:', user);
  }, [reduxStatusCounts, dashboardMetrics, dashboardData, user, showUserStats]);

  // Fetch dashboard data for both All Projects and My Projects when date range changes
  useEffect(() => {
    setLoadingPaymentData(true);
    fetchDashboardData();
  }, [fetchDashboardData, location.key]);

  // Set up long polling for real-time data updates
  useEffect(() => {
    if (!isPolling) return;

    console.log('Setting up dashboard polling with interval:', POLLING_INTERVAL);

    const intervalId = setInterval(() => {
      console.log('Polling dashboard data...');
      fetchDashboardData();
    }, POLLING_INTERVAL);

    // Cleanup interval on unmount or when polling is disabled
    return () => {
      console.log('Cleaning up dashboard polling interval');
      clearInterval(intervalId);
    };
  }, [isPolling, fetchDashboardData]);

  // Update local state when user dashboard data changes
  useEffect(() => {
    if (dashboardData && showUserStats) {
      console.log('Updating local state with user dashboard data');
      // Update local state with the user dashboard data
      const paymentReceived = dashboardData.financialMetrics?.paymentReceivedSum;
      const billed = dashboardData.financialMetrics?.billedSum;
      const pipeline = dashboardData.financialMetrics?.pipelineSum;

      // Ensure we don't store NaN values
      setTotalPaymentAmount(isNaN(paymentReceived) ? 0 : paymentReceived || 0);
      setTotalBilledAmount(isNaN(billed) ? 0 : billed || 0);
      setTotalInProgressAmount(isNaN(pipeline) ? 0 : pipeline || 0);

      // Update status counts if available
      if (dashboardData.statusCounts) {
        console.log('Updating status counts from user dashboard data:', dashboardData.statusCounts);
        setStatusCounts(dashboardData.statusCounts);
      }

      setLoadingPaymentData(false);
    }
  }, [dashboardData, showUserStats]);

  // Update local state when dashboard metrics change, in a separate useEffect
  useEffect(() => {
    if (dashboardMetrics) {
      console.log('Updating local state with dashboard metrics');
      // Update local state with the new dashboard metrics structure
      const paymentReceived = dashboardMetrics.financialMetrics?.paymentReceivedSum;
      const billed = dashboardMetrics.financialMetrics?.billedSum;
      const pipeline = dashboardMetrics.financialMetrics?.pipelineSum;

      // Ensure we don't store NaN values
      setTotalPaymentAmount(isNaN(paymentReceived) ? 0 : paymentReceived || 0);
      setTotalBilledAmount(isNaN(billed) ? 0 : billed || 0);
      setTotalInProgressAmount(isNaN(pipeline) ? 0 : pipeline || 0);

      setStatusCounts(dashboardMetrics.statusCounts || {});
      setPaymentData(dashboardMetrics.dailyPayments || []);
      setLoadingPaymentData(false);
    }
  }, [dashboardMetrics]);

  // Handle status bar click - redirect to projects page with status filter
  const handleStatusClick = (status: string, index: number) => {
    // Map chart index to status name in the correct format for the API
    const statuses = [
      'UNASSIGNED',
      'ASSIGNED',
      'IN_PROGRESS', // Note: Using API format with underscore
      'REVIEW',
      'DELIVERED',
      'BILLED',
      'PAYMENT_RECEIVED', // Note: Using API format with underscore
      'REVISION',
      'REOPEN',
      'HOLD', // Note: API uses HOLD not ON-HOLD
      'CANCELLED',
    ];

    const apiStatus = statuses[index];

    // Convert API status format to display format for the Projects page
    let filterStatusForProjectsPage = '';
    switch (apiStatus) {
      case 'UNASSIGNED':
        filterStatusForProjectsPage = 'Unassigned';
        break;
      case 'ASSIGNED':
        filterStatusForProjectsPage = 'Assigned';
        break;
      case 'IN_PROGRESS':
        filterStatusForProjectsPage = 'In Progress';
        break;
      case 'REVIEW':
        filterStatusForProjectsPage = 'Review';
        break;
      case 'DELIVERED':
        filterStatusForProjectsPage = 'Delivered';
        break;
      case 'BILLED':
        filterStatusForProjectsPage = 'Billed';
        break;
      case 'PAYMENT_RECEIVED':
        filterStatusForProjectsPage = 'Payment Received';
        break;
      case 'REVISION':
        filterStatusForProjectsPage = 'Unpaid Reopen';
        break;
      case 'REOPEN':
        filterStatusForProjectsPage = 'Paid Reopen';
        break;
      case 'HOLD':
        filterStatusForProjectsPage = 'On Hold';
        break;
      case 'CANCELLED':
        filterStatusForProjectsPage = 'Cancelled';
        break;
      default:
        filterStatusForProjectsPage = apiStatus;
    }

    // Navigate to the appropriate page based on current view
    if (showUserStats) {
      // Navigate to My Projects page with status filter
      navigate('/my-projects', {
        state: {
          statusFilter: filterStatusForProjectsPage,
          fromDashboard: true,
        },
      });
    } else {
      // Navigate to All Projects page with status filter
      navigate('/projects', {
        state: {
          statusFilter: filterStatusForProjectsPage,
          fromDashboard: true,
        },
      });
    }
  };

  const formatDateForQuery = (date: Date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };

  const handleFinancialCardClick = (
    cardKey: 'PAYMENT_RECEIVED' | 'BILLED' | 'DUE' | 'IN_PIPELINE'
  ) => {
    // IN_PIPELINE navigates to projects page instead of billing history
    if (cardKey === 'IN_PIPELINE') {
      // Navigate to projects page - show ALL never-billed projects (lifetime, no date range)
      navigate('/projects', {
        state: {
          filterType: 'IN_PIPELINE',
          fromDashboard: true,
        },
      });
      return;
    }
    
    if (cardKey !== 'BILLED' && cardKey !== 'PAYMENT_RECEIVED' && cardKey !== 'DUE') {
      return;
    }

    const params = new URLSearchParams({
      page: '1',
      limit: '10',
      series: 'ALL',
    });

    if (cardKey === 'DUE') {
      // DUE: Show all invoices with pending payments (no date filter)
      params.set('includeDuePaymentPending', 'true');
    } else {
      // BILLED & PAYMENT_RECEIVED: Use date range filter
      const startDate = formatDateForQuery(currentDateRange.startDate);
      const endDate = formatDateForQuery(currentDateRange.endDate);
      params.set('startDate', startDate);
      params.set('endDate', endDate);
    }

    // BILLED: Show ALL invoices in date range (no payment filter)
    // PAYMENT_RECEIVED: Show only fully paid invoices
    if (cardKey === 'PAYMENT_RECEIVED') {
      params.set('includePaymentReceived', 'true');
    }
    // Note: BILLED doesn't set any payment filter to show all invoices

    navigate(`/billing-history?${params.toString()}`);
  };

  // Prepare payment chart data from API response or dashboard metrics
  const preparePaymentChartData = () => {
    // Select the appropriate data source based on the current view mode
    const apiData = showUserStats
      ? dashboardData?.dailyPayments || []
      : dashboardMetrics?.dailyPayments || paymentData;

    // Calculate date range
    const startDate = new Date(currentDateRange.startDate);
    const endDate = new Date(currentDateRange.endDate);

    // Calculate the difference in days
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Determine if we should group by month or show daily
    const groupByMonth = diffDays > 30;

    if (groupByMonth) {
      // Group by months
      const monthMap = new Map<string, number>();
      const labels: string[] = [];

      // Create all months in the range
      let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

      while (current <= endMonth) {
        const monthKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
        monthMap.set(monthKey, 0);
        labels.push(monthLabel);
        current.setMonth(current.getMonth() + 1);
      }

      // Aggregate payment data by month
      apiData.forEach((item: PaymentData) => {
        const date = new Date(item.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (monthMap.has(monthKey)) {
          monthMap.set(monthKey, (monthMap.get(monthKey) || 0) + (item.amount || 0));
        }
      });

      const amounts = Array.from(monthMap.values());

      return {
        labels,
        datasets: [
          {
            label: 'Payment Received',
            data: amounts,
            backgroundColor: amounts.map(amount =>
              amount > 0 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(209, 213, 219, 0.4)'
            ),
            borderColor: 'transparent',
            borderWidth: 0,
            borderRadius: 2,
            barThickness: 'flex',
          },
        ],
      };
    } else {
      // Show daily data - create entry for each day in range
      const dailyMap = new Map<string, number>();
      const labels: string[] = [];
      const dateKeys: string[] = [];

      // Helper function to get date key without timezone issues
      const getLocalDateKey = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Create all days in the range
      let current = new Date(startDate);
      while (current <= endDate) {
        const dateKey = getLocalDateKey(current);
        const dayLabel = current.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
        dailyMap.set(dateKey, 0);
        dateKeys.push(dateKey);
        labels.push(dayLabel);
        current.setDate(current.getDate() + 1);
      }

      // Fill in actual payment amounts - handle different date formats
      apiData.forEach((item: PaymentData) => {
        const itemDate = new Date(item.date);
        const dateKey = getLocalDateKey(itemDate);

        if (dailyMap.has(dateKey)) {
          dailyMap.set(dateKey, (dailyMap.get(dateKey) || 0) + (item.amount || 0));
        }
      });

      // Get amounts in the correct order
      const amounts = dateKeys.map(key => dailyMap.get(key) || 0);

      return {
        labels,
        datasets: [
          {
            label: 'Payment Received',
            data: amounts,
            backgroundColor: amounts.map(amount =>
              amount > 0 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(209, 213, 219, 0.4)'
            ),
            borderColor: 'transparent',
            borderWidth: 0,
            borderRadius: 2,
            barThickness: 'flex',
          },
        ],
      };
    }
  };

  // Total Projects data - using the original data format
  const totalProjectsData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Total Projects',
        data: [
          9800,
          10000,
          10200,
          10300,
          10400,
          10450,
          10500,
          10800,
          11000,
          11200,
          11400,
          isNaN(projectCount) ? 0 : projectCount || 0,
        ],
        borderColor: 'rgba(99, 102, 241, 1)',
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Project Status data - use actual data from Redux store
  const projectStatusData = {
    labels: [
      'UN-ASSIGNED',
      'ASSIGNED',
      'IN-PROGRESS',
      'REVIEW',
      'DELIVERED',
      'BILLED',
      'PAYMENT RECEIVED',
      'REVISION',
      'REOPEN',
      'ON-HOLD',
      'CANCELLED',
    ],
    datasets: [
      {
        data: [
          // Use user-specific status counts when showUserStats is true, otherwise use global counts from new API
          // Note: Backend uses underscores in status keys (e.g., 'IN_PROGRESS') while frontend display uses hyphens
          showUserStats
            ? dashboardData?.statusCounts?.UNASSIGNED || 0
            : dashboardMetrics?.statusCounts?.UNASSIGNED ||
            (reduxStatusCounts || {}).UNASSIGNED ||
            0,
          showUserStats
            ? dashboardData?.statusCounts?.ASSIGNED || 0
            : dashboardMetrics?.statusCounts?.ASSIGNED || (reduxStatusCounts || {}).ASSIGNED || 0,
          showUserStats
            ? dashboardData?.statusCounts?.IN_PROGRESS || 0
            : dashboardMetrics?.statusCounts?.IN_PROGRESS ||
            (reduxStatusCounts || {}).IN_PROGRESS ||
            0,
          showUserStats
            ? dashboardData?.statusCounts?.REVIEW || 0
            : dashboardMetrics?.statusCounts?.REVIEW || (reduxStatusCounts || {}).REVIEW || 0,
          showUserStats
            ? dashboardData?.statusCounts?.DELIVERED || 0
            : dashboardMetrics?.statusCounts?.DELIVERED || (reduxStatusCounts || {}).DELIVERED || 0,
          showUserStats
            ? dashboardData?.statusCounts?.BILLED || 0
            : dashboardMetrics?.statusCounts?.BILLED || (reduxStatusCounts || {}).BILLED || 0,
          showUserStats
            ? dashboardData?.statusCounts?.PAYMENT_RECEIVED || 0
            : dashboardMetrics?.statusCounts?.PAYMENT_RECEIVED ||
            (reduxStatusCounts || {}).PAYMENT_RECEIVED ||
            0,
          showUserStats
            ? dashboardData?.statusCounts?.REVISION || 0
            : dashboardMetrics?.statusCounts?.REVISION || (reduxStatusCounts || {}).REVISION || 0,
          showUserStats
            ? dashboardData?.statusCounts?.REOPEN || 0
            : dashboardMetrics?.statusCounts?.REOPEN || (reduxStatusCounts || {}).REOPEN || 0,
          showUserStats
            ? dashboardData?.statusCounts?.HOLD || 0
            : dashboardMetrics?.statusCounts?.HOLD || (reduxStatusCounts || {}).HOLD || 0,
          showUserStats
            ? dashboardData?.statusCounts?.CANCELLED || 0
            : dashboardMetrics?.statusCounts?.CANCELLED || (reduxStatusCounts || {}).CANCELLED || 0,
        ],
        backgroundColor: [
          '#9CA3AF', // gray for UN-ASSIGNED
          '#A78BFA', // purple for ASSIGNED
          '#4F46E5', // indigo for IN-PROGRESS
          '#F59E0B', // amber for REVIEW
          '#10B981', // green for DELIVERED
          '#06B6D4', // cyan for BILLED
          '#047857', // emerald for PAYMENT RECEIVED
          '#9333EA', // purple for REVISION
          '#3B82F6', // blue for REOPEN
          '#6B7280', // gray for ON-HOLD
          '#EF4444', // red for CANCELLED
        ],
        borderWidth: 0,
        borderRadius: 4,
      },
    ],
  };

  // Options for line chart (Total Projects) - using original options
  const totalProjectsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
      datalabels: {
        display: false, // Disable datalabels for this chart
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
    },
  };

  // Options for payments bar chart
  const paymentsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        padding: 10,
        titleColor: '#fff',
        bodyColor: '#fff',
        titleFont: {
          size: 11,
          weight: '600' as const,
        },
        bodyFont: {
          size: 13,
          weight: 'bold' as const,
        },
        displayColors: false,
        callbacks: {
          title: function (context: any) {
            return context[0].label;
          },
          label: function (context: any) {
            const value = context.parsed.y;
            return formatCurrency(isNaN(value) ? 0 : value);
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          display: true,
          color: '#6B7280',
          font: {
            size: 9,
          },
          maxRotation: 90,
          minRotation: 45,
          autoSkip: true,
          maxTicksLimit: 20,
        },
      },
      y: {
        display: true,
        position: 'right' as const,
        beginAtZero: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          callback: function (value: any) {
            if (value >= 100000) {
              return '₹' + (value / 100000).toFixed(1) + 'L';
            }
            if (value >= 1000) {
              return '₹' + (value / 1000).toFixed(0) + 'k';
            }
            return '₹' + value;
          },
        },
      },
    },
    barPercentage: 0.95,
    categoryPercentage: 0.98,
  };

  // Options for project status bar chart
  const projectStatusOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 9,
          },
        },
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false,
        },
        ticks: {
          color: '#6B7280',
          font: {
            size: 10,
          },
          // Calculate stepSize based on max value
          callback: function (value: number) {
            return value.toString();
          },
        },
        // Don't set max - let it be determined by data
        beginAtZero: true,
        // Suggest the number of steps but let chart.js decide
        suggestedMax: Math.max(...projectStatusData.datasets[0].data) * 1.1,
      },
    },
    barPercentage: 0.9,
    categoryPercentage: 0.9,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context: any) {
            const value = context.raw;
            // Handle NaN values in tooltip
            return isNaN(value) ? '0' : value.toString();
          },
        },
      },
      datalabels: {
        anchor: 'end' as const,
        align: 'end' as const,
        offset: -1, // Positioning slightly inside the bar
        formatter: (value: any) => (isNaN(value) ? '0' : value),
        font: {
          weight: 'bold' as const,
          size: 12,
        },
        backgroundColor: 'white',
        borderRadius: 12,
        padding: {
          top: 4,
          bottom: 2,
          left: 10,
          right: 10,
        },
        borderWidth: 1,
        borderColor: (context: any) => {
          // Get the background color of the current bar
          const barColor = context.dataset.backgroundColor[context.dataIndex];
          return barColor;
        },
        color: (context: any) => {
          // Get the background color of the current bar to make the text match
          return context.dataset.backgroundColor[context.dataIndex];
        },
        // Display labels overlapping the top of the bars
        display: (context: any) => {
          return true;
        },
        // Custom positioning to make the label straddle the top of the bar
        y: (context: any) => {
          return -18; // Adjust this value to control how much of the label shows outside the bar
        },
        // Make labels clickable by adding listeners
        listeners: {
          click: (context: any) => {
            const index = context.dataIndex;
            const status = projectStatusData.labels[index] as string;
            handleStatusClick(status, index);
          },
          enter: (context: any) => {
            // Change cursor to pointer when hovering over labels
            const chart = context.chart;
            if (chart.canvas) {
              chart.canvas.style.cursor = 'pointer';
            }
          },
          leave: (context: any) => {
            // Reset cursor when leaving labels
            const chart = context.chart;
            if (chart.canvas) {
              chart.canvas.style.cursor = 'default';
            }
          },
        },
      },
    },
    onClick: (event: any, elements: any) => {
      if (elements && elements.length > 0) {
        const clickedIndex = elements[0].index;
        const clickedStatus = projectStatusData.labels[clickedIndex] as string;
        handleStatusClick(clickedStatus, clickedIndex);
      }
    },
    // Add cursor pointer to indicate clickable for bars
    onHover: (event: any, elements: any) => {
      if (event.native) {
        event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },
  };

  // Get the last payment amount for display
  const getLastPaymentAmount = () => {
    if (!paymentData || paymentData.length === 0) return 0;

    const sortedData = [...paymentData].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );

    // Make sure we have an amount and it's a valid number
    const amount = sortedData[0]?.amount;
    return isNaN(amount) ? 0 : amount || 0;
  };

  // Generate 5-6 evenly distributed dates from the date range to display below the chart
  const getDistributedDates = () => {
    const startDate = currentDateRange.startDate;
    const endDate = currentDateRange.endDate;
    const totalDays = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));

    // Determine how many dates to display (between 5-6)
    const numDates = totalDays <= 5 ? totalDays + 1 : 6;

    // If we have very few days, just show each day
    if (numDates <= 6 && numDates > 1) {
      const dates = [];
      for (let i = 0; i < numDates; i++) {
        const date = new Date(startDate);
        const increment =
          i === numDates - 1 ? totalDays : Math.floor((totalDays * i) / (numDates - 1));
        date.setDate(startDate.getDate() + increment);
        dates.push(date);
      }
      return dates;
    }

    // For larger ranges, distribute 6 dates evenly
    const dates = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date(startDate);
      const increment = i === 5 ? totalDays : Math.floor((totalDays * i) / 5);
      date.setDate(startDate.getDate() + increment);
      dates.push(date);
    }
    return dates;
  };

  // Format date for display in the date markers
  const formatDateForMarker = (date: Date) => {
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
  };

  // Prepare payment chart data
  const currentPaymentsData = preparePaymentChartData();

  // Format status text for display
  const formatStatusText = (status: string): string => {
    const formattedStatus = status.replace(/_/g, '-');

    switch (formattedStatus) {
      case 'UNASSIGNED':
        return 'Un-assigned';
      case 'IN-PROGRESS':
        return 'In-Progress';
      case 'PAYMENT-RECEIVED':
        return 'Payment Received';
      case 'REVISION':
        return 'Revision';
      case 'REOPEN':
        return 'Reopen';
      default:
        return formattedStatus.charAt(0) + formattedStatus.slice(1).toLowerCase();
    }
  };

  // Format date to DD-MMM-YY format
  const formatDateToDDMMYY = (dateInput: string | Date | undefined): string => {
    if (!dateInput) return 'N/A';
    const date = new Date(dateInput);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
  };

  // Extract time in 12-hour format with AM/PM
  const extractTimeFromDate = (dateInput: string | Date | undefined): string => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Open image viewer
  const openImageViewer = (imageUrl: string) => {
    setViewerImage(imageUrl);
    setIsImageViewerOpen(true);
  };

  // Close image viewer
  const closeImageViewer = () => {
    setIsImageViewerOpen(false);
    setViewerImage('');
  };

  // Latest Projects Table Component
  const LatestProjectsTable = () => {
    // Get current user ID
    const currentUserId = user?.id;

    // Use userProjects from Redux state instead of filtering from allProjects
    // Add safety checks to prevent "Cannot read properties of undefined" error
    const userProjectsList =
      userProjects && Array.isArray(userProjects) && userProjects.length > 0
        ? userProjects.slice(0, 5) // Get latest 5 projects
        : [];

    // Status colors mapping (consistent with Projects page)
    const statusColors = {
      'In Progress': 'bg-blue-100 text-blue-800',
      Delivered: 'bg-green-100 text-green-800',
      Review: 'bg-yellow-100 text-yellow-800',
      Assigned: 'bg-indigo-100 text-indigo-800',
      Billed: 'bg-red-100 text-red-800',
      Hold: 'bg-orange-100 text-orange-800',
      Cancelled: 'bg-red-100 text-red-800',
      'Payment Received': 'bg-green-100 text-green-800',
      Revision: 'bg-purple-100 text-purple-800',
      Reopen: 'bg-blue-100 text-blue-800',
      Unassigned: 'bg-gray-100 text-gray-800',
      'Non Active': 'bg-gray-200 text-gray-700',
      // Original backend versions
      IN_PROGRESS: 'bg-blue-100 text-blue-800',
      DELIVERED: 'bg-green-100 text-green-800',
      REVIEW: 'bg-yellow-100 text-yellow-800',
      ASSIGNED: 'bg-indigo-100 text-indigo-800',
      BILLED: 'bg-red-100 text-red-800',
      HOLD: 'bg-orange-100 text-orange-800',
      CANCELLED: 'bg-red-100 text-red-800',
      PAYMENT_RECEIVED: 'bg-green-100 text-green-800',
      REVISION: 'bg-purple-100 text-purple-800',
      REOPEN: 'bg-blue-100 text-blue-800',
      UNASSIGNED: 'bg-gray-100 text-gray-800',
      NON_ACTIVE: 'bg-gray-200 text-gray-700',
    };

    // Status icons mapping
    const statusIcons = {
      'In Progress': <TbProgress className="w-4 h-4" />,
      Delivered: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
      Review: <LiaNotesMedicalSolid className="w-4 h-4" />,
      Assigned: <BiReceipt className="w-4 h-4" />,
      Billed: <BiReceipt className="w-4 h-4" />,
      Hold: <AiOutlineClockCircle className="w-4 h-4" />,
      Cancelled: <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
      'Payment Received': <IoCheckmarkDoneCircleOutline className="w-4 h-4" />,
      Revision: <PiRepeatFill className="w-4 h-4" />,
      Reopen: <LiaNotesMedicalSolid className="w-4 h-4" />,
      Unassigned: <MdOutlineWorkOutline className="w-4 h-4" />,
      'Non Active': <MdOutlineWorkOutline className="w-4 h-4" />,
    };

    // Helper function to get latest project status
    const getLatestProjectStatus = (project: any): string => {
      if (project.projectAssigned && project.projectAssigned.length > 0) {
        return project.projectAssigned[0].status || 'UNASSIGNED';
      }
      return 'UNASSIGNED';
    };

    // Helper function to get Manak ID from project
    const getManakId = (project: any) => {
      return (
        project.manakProjectId || project.manakId || project.id?.toString()?.slice(-4) || 'N/A'
      );
    };

    // Helper function to get revision data
    const getRevisionInfo = (project: any) => {
      if (project.projectRevisions && project.projectRevisions.length > 0) {
        return {
          hasRevision: true,
          number: project.projectRevisions[0].revisionNumber,
          formatted:
            project.projectRevisions[0].formattedRevisionNumber ||
            `R${project.projectRevisions[0].revisionNumber}`,
        };
      } else if (project.revisionNumber && project.revisionNumber > 0) {
        return {
          hasRevision: true,
          number: project.revisionNumber,
          formatted: `R${project.revisionNumber}`,
        };
      }
      return { hasRevision: false };
    };

    // Helper function to get customer name using utility
    const getCustomerName = (project: any) => {
      return extractCustomerName(project, customers);
    };

    // Helper function to get project name
    const getProjectName = (project: any) => {
      return project.name || project.projectName || 'Untitled Project';
    };

    // Helper function to format date
    const formatProjectDate = (project: any) => {
      const dateStr = project.startDate || project.createdAt;
      if (!dateStr) return { date: 'N/A', time: '' };

      const date = new Date(dateStr);
      return {
        date: formatDateToDDMMYY(date),
        time: extractTimeFromDate(date),
      };
    };

    // Handle project click to show popup
    const handleProjectClick = (project: any) => {
      setSelectedProject(project);
      projectDetailsPopup.openPopup();
    };

    // Column definitions matching Projects page
    const columns = [
      {
        key: 'id',
        header: 'M ID',
        icon: <GoHash />,
        width: '10%',
        headerClassName: 'font-bold text-sm',
        renderCell: (value: any, row: any) => (
          <div className="flex items-center">
            <span className="flex-shrink-0">{getManakId(row)}</span>
            {(() => {
              const revInfo = getRevisionInfo(row);
              return revInfo.hasRevision ? (
                <span className="ml-2 bg-purple-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0">
                  {revInfo.formatted}
                </span>
              ) : null;
            })()}
          </div>
        ),
      },
      {
        key: 'customerName',
        header: 'Company Name',
        icon: <CiUser />,
        width: '20%',
        headerClassName: 'font-bold text-sm',
        truncate: true,
        renderCell: (value: any, row: any) => getCustomerName(row),
      },
      {
        key: 'articleNo',
        header: 'Article No',
        icon: <FaRegFileAlt />,
        width: '15%',
        headerClassName: 'font-bold text-sm',
        renderCell: (value: any, row: any) => row.projectNumber || row.articleNo || 'N/A',
      },
      {
        key: 'projectName',
        header: 'Project Name',
        icon: <MdOutlineFeaturedPlayList />,
        width: '20%',
        headerClassName: 'font-bold text-sm',
        truncate: true,
        renderCell: (value: any, row: any) => getProjectName(row),
      },
      {
        key: 'createdOn',
        header: 'Created At',
        icon: <AiOutlineClockCircle />,
        width: '15%',
        headerClassName: 'font-bold text-sm',
        renderCell: (value: any, row: any) => {
          const dateInfo = formatProjectDate(row);
          return (
            <div>
              <div className="text-sm">{dateInfo.date}</div>
              <div className="text-xs text-gray-400">{dateInfo.time}</div>
            </div>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        icon: <TbProgress />,
        width: '20%',
        headerClassName: 'font-bold text-sm',
        renderCell: (value: any, row: any) => {
          const status = getLatestProjectStatus(row);
          const formattedStatus = formatStatusText(status);
          const colorClass =
            statusColors[formattedStatus] || statusColors[status] || 'bg-gray-100 text-gray-800';
          const icon = statusIcons[formattedStatus] || statusIcons[status];

          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}
            >
              {icon && <span className="mr-1">{icon}</span>}
              {formattedStatus}
            </span>
          );
        },
      },
    ];

    // Convert projects to table data format
    const tableData = userProjectsList.map(project => ({
      ...project,
      id: getManakId(project),
      customerName: getCustomerName(project),
      projectName: getProjectName(project),
      status: formatStatusText(getLatestProjectStatus(project)),
      createdOn: formatProjectDate(project),
    }));

    if (userProjectsLoading) {
      return (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }

    if (userProjectsList.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <FiFileText className="mx-auto text-4xl mb-4 text-gray-400" />
          <p className="text-lg font-medium">No projects assigned to you yet</p>
          <p className="text-sm">Projects assigned to you will appear here</p>
        </div>
      );
    }

    return (
      <ResponsiveTable
        data={tableData}
        columns={columns}
        onRowClick={handleProjectClick}
        isLoading={userProjectsLoading}
        emptyText="No projects assigned to you"
        className="border-0"
      />
    );
  };

  return (
    <div className="container mx-auto p-4">
      {/* First row - Key Stats: Total, Billed, Payment Received, Cancelled */}
      <DashboardStatsRow
        totalProjects={displayProjectCount}
        billedCount={
          showUserStats
            ? dashboardData?.statusCounts?.BILLED || 0
            : dashboardMetrics?.statusCounts?.BILLED || reduxStatusCounts?.BILLED || 0
        }
        paymentReceivedCount={
          showUserStats
            ? dashboardData?.statusCounts?.PAYMENT_RECEIVED || 0
            : dashboardMetrics?.statusCounts?.PAYMENT_RECEIVED || reduxStatusCounts?.PAYMENT_RECEIVED || 0
        }
        cancelledCount={
          showUserStats
            ? dashboardData?.statusCounts?.CANCELLED || 0
            : dashboardMetrics?.statusCounts?.CANCELLED || reduxStatusCounts?.CANCELLED || 0
        }
        onStatusClick={handleStatusClick}
        loading={loadingPaymentData || projectsLoading}
      />

      {/* Second row - Financial Overview: only visible with financeAccess */}
      {hasFinanceAccess(user) && (
        <DashboardFinancialRow
          paymentReceived={displayPaymentReceived}
          billed={displayBilledAmount}
          due={displayDueAmount}
          inPipeline={displayPipelineAmount}
          loading={loadingPaymentData || projectsLoading}
          onCardClick={handleFinancialCardClick}
          clickableCardKeys={['BILLED', 'PAYMENT_RECEIVED', 'DUE', 'IN_PIPELINE']}
        />
      )}

      {/* Third row - Status Flow: Unassigned, Assigned, In Progress, Review, Delivered */}
      <DashboardStatusRow
        unassignedCount={
          showUserStats
            ? dashboardData?.statusCounts?.UNASSIGNED || 0
            : dashboardMetrics?.statusCounts?.UNASSIGNED || reduxStatusCounts?.UNASSIGNED || 0
        }
        assignedCount={
          showUserStats
            ? dashboardData?.statusCounts?.ASSIGNED || 0
            : dashboardMetrics?.statusCounts?.ASSIGNED || reduxStatusCounts?.ASSIGNED || 0
        }
        inProgressCount={
          showUserStats
            ? dashboardData?.statusCounts?.IN_PROGRESS || 0
            : dashboardMetrics?.statusCounts?.IN_PROGRESS || reduxStatusCounts?.IN_PROGRESS || 0
        }
        reviewCount={
          showUserStats
            ? dashboardData?.statusCounts?.REVIEW || 0
            : dashboardMetrics?.statusCounts?.REVIEW || reduxStatusCounts?.REVIEW || 0
        }
        deliveredCount={
          showUserStats
            ? dashboardData?.statusCounts?.DELIVERED || 0
            : dashboardMetrics?.statusCounts?.DELIVERED || reduxStatusCounts?.DELIVERED || 0
        }
        totalProjects={displayProjectCount}
        onStatusClick={handleStatusClick}
        loading={loadingPaymentData || projectsLoading}
      />

      {/* Fourth row - Project Reopens: On Hold, Unpaid Reopen, Paid Reopen */}
      <DashboardReopenRow
        onHoldCount={
          showUserStats
            ? dashboardData?.statusCounts?.HOLD || 0
            : dashboardMetrics?.statusCounts?.HOLD || reduxStatusCounts?.HOLD || 0
        }
        unpaidReopenCount={
          showUserStats
            ? dashboardData?.statusCounts?.REVISION || 0
            : dashboardMetrics?.statusCounts?.REVISION || reduxStatusCounts?.REVISION || 0
        }
        paidReopenCount={
          showUserStats
            ? dashboardData?.statusCounts?.REOPEN || 0
            : dashboardMetrics?.statusCounts?.REOPEN || reduxStatusCounts?.REOPEN || 0
        }
        onStatusClick={handleStatusClick}
        loading={loadingPaymentData || projectsLoading}
      />

      {/* Fifth row - Latest Projects assigned to current user */}
      <div className="bg-white p-6 rounded-2xl border-2 border-indigo-300 shadow-lg drop-shadow-[0_10px_15px_rgba(99,102,241,0.2)] mb-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center">
            <div className="text-indigo-600 mr-3">
              <FiFileText className="text-3xl" />
            </div>
            <h2 className="text-indigo-600 font-semibold text-3xl">Projects Assigned to You</h2>
          </div>
          <button
            onClick={() => navigate('/my-projects')}
            className="text-indigo-600 hover:text-indigo-800 text-sm font-medium underline"
          >
            View All →
          </button>
        </div>

        {/* Latest Projects Table */}
        <LatestProjectsTable />
      </div>

      {/* Project Details Popup using PopupWrapper component */}
      {selectedProject && (
        <PopupWrapper
          title={
            <div className="flex flex-col">
              <div className="flex items-center justify-between gap-4">
                <span className="text-4xl font-semibold">
                  M{selectedProject.manakProjectId ||
                    selectedProject.manakId ||
                    selectedProject.id?.toString()?.slice(-4) ||
                    'N/A'}
                </span>
                <div
                  className={`flex items-center gap-2 px-5 rounded-full text-sm ${statusColors[selectedProject.status || 'UNASSIGNED'] ||
                    'bg-gray-100 text-gray-800'
                    }`}
                >
                  <span className="w-4 h-4">
                    {statusIcons[selectedProject.status || 'UNASSIGNED']}
                  </span>
                  <span>{formatStatusText(selectedProject.status || 'UNASSIGNED')}</span>
                </div>
              </div>
              <div className="mt-1 text-sm text-gray-500">
                Last updated on -{' '}
                {formatDateToDDMMYY(
                  selectedProject.updatedAt ||
                  selectedProject.createdAt ||
                  selectedProject.startDate
                )}{' '}
                (
                {extractTimeFromDate(
                  selectedProject.updatedAt ||
                  selectedProject.createdAt ||
                  selectedProject.startDate
                )}
                )
              </div>
            </div>
          }
          isOpen={projectDetailsPopup.isOpen}
          onClose={projectDetailsPopup.closePopup}
          size="lg"
          logo={<FiFileText size={53} className="text-blue-600" />}
        >
          <div className="flex flex-col py-6">
            <div className="grid grid-cols-2 gap-6">
              <InputField
                label="Company Name"
                value={
                  selectedProject.customer?.name ||
                  selectedProject.customerName ||
                  (selectedProject.client ? selectedProject.client.name : 'N/A')
                }
                className="bg-gray-100"
                disabled
                icon={<CiUser className="w-5 h-5" />}
              />
              <InputField
                label="Article Number"
                value={selectedProject.articleNo || 'N/A'}
                className="bg-gray-100"
                disabled
                icon={<FaRegFileAlt className="w-5 h-5" />}
              />
              <InputField
                label="Project Name"
                value={selectedProject.name || selectedProject.projectName || 'Untitled Project'}
                className="bg-gray-100"
                disabled
                icon={<FiFileText className="w-5 h-5" />}
              />
              <InputField
                label="Assigned To"
                value={
                  selectedProject.assignedTo?.name ||
                  selectedProject.assignedEmployeeName ||
                  (selectedProject.employee ? selectedProject.employee.name : 'Not Assigned')
                }
                className="bg-gray-100"
                disabled
                icon={<MdOutlineWorkOutline className="w-5 h-5" />}
              />
              <InputField
                label="Received On"
                value={formatDateToDDMMYY(selectedProject.createdAt || selectedProject.startDate)}
                className="bg-gray-100"
                disabled
                icon={<AiOutlineClockCircle className="w-5 h-5" />}
              />

              {/* History button */}
              <button
                onClick={() => setShowAssignmentHistory(!showAssignmentHistory)}
                className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                  flex items-center justify-between
                  ${showAssignmentHistory
                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <FiClock className="h-5 w-5" />
                  <span className="font-medium">View History</span>
                </div>
                {showAssignmentHistory ? (
                  <FiChevronUp className="h-5 w-5" />
                ) : (
                  <FiChevronDown className="h-5 w-5" />
                )}
              </button>
            </div>

            {/* Assignment History Table */}
            <div
              className={`mt-3 transition-all duration-300 ${showAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
            >
              <div className="bg-blue-50 rounded-lg p-4 text-blue-800">
                <div className="flex items-center mb-2">
                  <FiClock className="mr-2" />
                  <h3 className="font-semibold">Project History</h3>
                </div>
                <p>
                  This simplified view shows only the current status. For detailed history, please
                  use the full project view.
                </p>
              </div>
            </div>

            {/* Product Attachments */}
            <div className="py-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Product Attachments</h3>
              <div className="flex flex-wrap gap-4">
                {selectedProject.profilePicture ? (
                  <div className="relative">
                    <img
                      src={selectedProject.profilePicture}
                      alt={`Project ${selectedProject.name || selectedProject.projectName} Image`}
                      className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => openImageViewer(selectedProject.profilePicture)}
                    />
                  </div>
                ) : selectedProject.projectImages?.length > 0 ? (
                  selectedProject.projectImages.map((image: string, index: number) => (
                    <div key={index} className="relative">
                      <img
                        src={image}
                        alt={`Project Attachment ${index + 1}`}
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => openImageViewer(image)}
                      />
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">No attachments available</p>
                )}
              </div>
            </div>

            {/* Description Section - if available */}
            {selectedProject.description && (
              <div className="py-2">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Description</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-800 whitespace-pre-line">{selectedProject.description}</p>
                </div>
              </div>
            )}
          </div>
        </PopupWrapper>
      )}

      {/* Image Viewer */}
      <ImageViewer isOpen={isImageViewerOpen} imageUrl={viewerImage} onClose={closeImageViewer} />
    </div>
  );
};

export default Dashboard;
